import { Component, ElementRef, inject, input, output, signal, viewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { BauherrHelper } from '../../core/helper/bauherr.helper';
import { ProjektDto } from '../../core/models/projekt/projekt-dto.model';
import { SearchBoxDatabinding } from '../../core/models/shared/search-box-databinding.model';
import { ToastService } from '../../core/services/toast.service';
import { LoaderComponent } from '../loader/loader.component';
import { PaginationComponent } from '../pagination/pagination.component';

@Component({
  selector: 'bpw-search-box',
  standalone: true,
  imports: [FormsModule, LoaderComponent, PaginationComponent],
  templateUrl: './search-box.component.html',
  styleUrls: ['./search-box.component.scss'],
})
export class SearchBoxComponent {
  private readonly _pagination = viewChild<PaginationComponent>('pagination');
  private readonly _searchElement = viewChild<ElementRef>('search');

  readonly databinding = input.required<SearchBoxDatabinding>();
  readonly rowClicked = output<any>();

  readonly searchTerm = signal('');
  readonly filteredData = signal<any[]>([]);
  readonly displayedData = signal<any[]>([]);
  readonly resultLoaderVisible = signal(false);
  readonly selectedRow = signal<any>(null);
  readonly selectedRowDisplayText = signal('');

  private readonly _keyClearSearchTerm = '*clear*';
  private readonly _toastService = inject(ToastService);

  readonly search$ = new Subject<string>();

  constructor() {
    this.search$
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        switchMap((searchTerm) => {
          if (searchTerm === this._keyClearSearchTerm) {
            this._toastService.clear();
            return new Observable();
          }

          const db = this.databinding();
          if (db.searchTermMinLength > 0) {
            if (searchTerm.length > 0 && searchTerm.length < db.searchTermMinLength) {
              this.resultLoaderVisible.set(false);
              this._toastService.info('Suchbegriff zu kurz, mindestens ' + db.searchTermMinLength + ' Zeichen');
              return new Observable();
            }
          }

          this.resultLoaderVisible.set(true);
          this._toastService.clear();
          if (db.useSeperateGetDataForEmptySearch && searchTerm.length === 0) {
            return db.getEmptySearchData();
          }
          return db.getData(searchTerm);
        }),
      )
      .subscribe((result) => {
        this.handleResult(result);
      });
  }

  private handleResult(result: any) {
    const db = this.databinding();
    switch (result) {
      // Nützlich wenn man bei leerem Suchstring keine Resultate anzeigen will, vgl. ad-kontakt-selection.component.ts
      case 'Leeren':
        db.data = [];
        this.refreshData();
        // Notwendig, da wir nicht wollen das "Keine Daten gefunden" angezeigt wird
        db.status = 1;
        break;
      case 'Fehler':
        db.data = [];
        db.status = -1;
        break;
      default:
        db.handleResult(result);
        this.refreshData();
        break;
    }
    this.resultLoaderVisible.set(false);
  }

  public clear() {
    this.filteredData.set([]);
    this.displayedData.set([]);
    this.selectedRow.set(null);
    this.selectedRowDisplayText.set('');
    this.searchTerm.set('');
    const db = this.databinding();
    if (db.useRefetchFromServerMode) {
      this.search$.next(this._keyClearSearchTerm);
      db.clear();
    }
    this._pagination()?.clear();
  }

  public executeDirectSearch(searchTerm: string) {
    this.searchTerm.set(searchTerm);
    const db = this.databinding();
    if (db.useRefetchFromServerMode) {
      if (db.useSeperateGetDataForEmptySearch && searchTerm.length === 0) {
        db.getEmptySearchData().subscribe((result) => {
          this.handleResult(result);
        });
      } else {
        db.getData(searchTerm).subscribe((result) => {
          this.handleResult(result);
        });
      }
    } else {
      this.filterData(searchTerm);
    }
  }

  searchChanged(searchTerm: string) {
    this.searchTerm.set(searchTerm);
    const db = this.databinding();
    if (db.useRefetchFromServerMode) {
      this.search$.next(searchTerm);
    } else {
      this.filterData(searchTerm);
    }
  }

  clearSearch() {
    this.searchChanged('');
  }

  private refreshData() {
    const db = this.databinding();
    this.filteredData.set(db.data ?? []);
    db.status = this.filteredData().length > 0 ? 1 : 0;
    this.updatePagination();
  }

  private filterData(searchTerm: string) {
    const db = this.databinding();
    if (db.customColumns) {
      db.data = [];
      db.status = -2;
      return;
    }

    // Wenn die Daten für die Searchbox nur einmal geladen werden und dies fehlgeschlagen ist, dann ist sowieso keine Suche möglich
    if (db.status === -1) {
      return;
    }

    this.resultLoaderVisible.set(true);
    this.filteredData.set(db.data ?? []);
    if (searchTerm.length > 0) {
      this.filteredData.set(
        (db.data ?? []).filter((item) => {
          const searchText = searchTerm.toLowerCase();
          return (
            (item.dokument && item.dokument.toLowerCase().indexOf(searchText) !== -1) ||
            (item.bezeichnung && item.bezeichnung.toLowerCase().indexOf(searchText) !== -1)
          );
        }),
      );
    }
    db.status = this.filteredData().length > 0 ? 1 : 0;
    this.updatePagination();
    this.resultLoaderVisible.set(false);
  }

  private updatePagination() {
    this._pagination()?.updateTotalItemCount(this.filteredData().length);
  }

  selectedPageChanged(data: { begin: number; end: number }) {
    this.displayedData.set(this.filteredData().slice(data.begin, data.end));
  }

  rowClickedInternal(row: any) {
    this.selectedRow.set(row);
    const db = this.databinding();
    if (db.customColumns) {
      this.selectedRowDisplayText.set(db.getSelectedRowFormat(row));
    } else {
      this.selectedRowDisplayText.set(row.dokument + ' - ' + row.bezeichnung);
    }
    this.rowClicked.emit(row);
  }

  dropdownToggle() {
    const db = this.databinding();
    db.dropDownShown = !db.dropDownShown;
    if (db.dropDownShown) {
      this.focusSearchInput();
    }
  }

  popUp() {
    this.databinding().dropDownShown = true;
    this.focusSearchInput();
  }

  private focusSearchInput() {
    setTimeout(() => {
      this._searchElement()?.nativeElement.focus();
    }, 0);
  }

  getBauherr(projekt: ProjektDto): string | undefined {
    return BauherrHelper.getBauherrByProjektDto(projekt);
  }
}
